import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

import ChatHeader from './chat/ChatHeader';
import CustomerInfo from './chat/CustomerInfo';
import MessageList from './chat/MessageList';
import ChatInput from './chat/ChatInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/chat';

/**
 * ChatWidget — top-level orchestrator.
 * Owns all chat state (conversation, messages, WebSocket) and delegates
 * presentation to ChatHeader / CustomerInfo / MessageList / ChatInput.
 */
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [wsConnected, setWsConnected] = useState(false);
  const [agentName, setAgentName] = useState('');

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch and periodically refresh the global agent name
  const fetchAgentName = useCallback(() => {
    axios.get(`${API}/chat/agent-name`).then(res => {
      if (res.data.agent_name) setAgentName(res.data.agent_name);
    }).catch((err) => console.warn('agent-name fetch failed:', err?.message || err));
  }, []);

  useEffect(() => {
    fetchAgentName();
    const interval = setInterval(fetchAgentName, 30000);
    return () => clearInterval(interval);
  }, [fetchAgentName]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket connection
  const connectWebSocket = useCallback((convId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/${convId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
      ws._pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => {
            if (prev.some(m => m._id === data.message._id)) return prev;
            return [...prev, data.message];
          });
        } else if (data.type === 'chat_closed') {
          setConversation(prev => prev ? { ...prev, status: 'closed' } : prev);
        } else if (data.type === 'chat_accepted') {
          setConversation(prev => prev ? { ...prev, status: 'active', assigned_agent: data.assigned_agent } : prev);
        }
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      clearInterval(ws._pingInterval);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (conversation?._id) connectWebSocket(convId);
      }, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [conversation]);

  useEffect(() => {
    if (conversation?._id && isOpen) {
      connectWebSocket(conversation._id);
    }
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [conversation?._id, isOpen, connectWebSocket]);

  const startConversation = async (e) => {
    e.preventDefault();
    if (!userInfo.name || !userInfo.email) {
      toast.error('Please enter your name and email');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chat/conversations`, {
        name: userInfo.name,
        email: userInfo.email,
      });
      if (response.data.success) {
        setConversation(response.data.conversation);
        setMessages(response.data.conversation.messages || []);
        setShowForm(false);
        localStorage.setItem('chat_user', JSON.stringify(userInfo));
        localStorage.setItem('chat_conversation', response.data.conversation._id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start chat');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation) return;

    const msgContent = newMessage;
    setNewMessage('');
    setIsSending(true);
    try {
      const response = await axios.post(`${API}/chat/messages`, {
        conversation_id: conversation._id,
        content: msgContent,
        sender_type: 'customer',
      });
      if (response.data.success && !wsConnected) {
        setMessages(prev => {
          if (prev.some(m => m._id === response.data.message._id)) return prev;
          return [...prev, response.data.message];
        });
      }
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(msgContent);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    try {
      const uploadResponse = await axios.post(`${API}/chat/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (uploadResponse.data.success) {
        await axios.post(`${API}/chat/messages`, {
          conversation_id: conversation._id,
          content: `File: ${file.name}`,
          sender_type: 'customer',
          file_url: uploadResponse.data.file_url,
        });
      }
    } catch (error) {
      toast.error('Failed to upload file');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Load saved conversation on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('chat_user');
    const savedConvId = localStorage.getItem('chat_conversation');

    if (savedUser) setUserInfo(JSON.parse(savedUser));

    if (savedConvId && savedUser) {
      setShowForm(false);
      axios.get(`${API}/chat/conversations/${savedConvId}`)
        .then(response => {
          if (response.data.success) {
            setConversation(response.data.conversation);
            setMessages(response.data.conversation.messages || []);
          }
        })
        .catch(() => {
          localStorage.removeItem('chat_conversation');
          setShowForm(true);
        });
    }
  }, []);

  // Periodically refresh conversation to pick up assigned_agent changes
  useEffect(() => {
    if (!conversation?._id || !isOpen) return;
    const interval = setInterval(() => {
      axios.get(`${API}/chat/conversations/${conversation._id}`)
        .then(res => {
          if (res.data.success && res.data.conversation) {
            setConversation(res.data.conversation);
          }
        })
        .catch((err) => console.warn('conversation refresh failed:', err?.message || err));
    }, 15000);
    return () => clearInterval(interval);
  }, [conversation?._id, isOpen]);

  const newChat = () => {
    localStorage.removeItem('chat_conversation');
    setConversation(null);
    setMessages([]);
    setShowForm(true);
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
  };

  const unreadCount = conversation
    ? messages.filter(m => m.sender_type === 'admin' && !m.read).length
    : 0;

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-green-600 hover:bg-green-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-110"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="chat-widget">
          <ChatHeader
            conversation={conversation}
            hasConversation={!!conversation}
            onNewChat={newChat}
            onClose={() => setIsOpen(false)}
          />

          <div className="h-96 flex flex-col">
            {showForm ? (
              <CustomerInfo
                userInfo={userInfo}
                onChange={setUserInfo}
                onSubmit={startConversation}
                isLoading={isLoading}
              />
            ) : (
              <>
                <MessageList
                  messages={messages}
                  userInfo={userInfo}
                  messagesEndRef={messagesEndRef}
                />
                <ChatInput
                  conversation={conversation}
                  newMessage={newMessage}
                  onMessageChange={setNewMessage}
                  onSend={sendMessage}
                  isSending={isSending}
                  fileInputRef={fileInputRef}
                  onFileUpload={handleFileUpload}
                  onNewChat={newChat}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
